"use client";
import { talentNetworkText } from "@/i18n/talentNetwork.constants";
import messages from "@/i18n/messages/en.json";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { normalizePhoneInputValue } from "@artha/ui";
import { type CandidateContactStatusDto } from "@artha/api-contracts";
import { toast } from "sonner";
import { isValidWhatsAppNumber, normalizeCountryCode } from "@artha/utils";

import { fetchCandidateSession, isCandidateIdentityConflict } from "../services/personalizationClient";
import { fetchTalentNetworkSession } from "../../talent-network/services/talentNetworkClient";
import {
  candidateProfileFingerprint,
  hasVerifiedCandidateEmail,
  hasVerifiedCandidatePhone,
} from "../personalization.utils";
import { startCandidateContactVerification } from "../services/contactVerificationClient";
import {
  clearCandidateProfileDraft,
  saveCandidateProfileDraft,
} from "../services/profileDraft";
import {
  TALENT_NETWORK_CONSENT_STORAGE_PREFIX,
  TALENT_NETWORK_JOINED_QUERY_PARAM,
} from "../../talent-network/talentNetwork.constants";

import type {
  CandidateProfileFormProps,
  CandidateProfileVerification,
  CandidateContactField,
  CandidateContactDeliveryState,
} from "../personalization.types";
export function useCandidateProfileForm({
  username,
  source,
  controller,
  draft,
  session,
}: CandidateProfileFormProps) {
  const router = useRouter();
  const t = useTranslations("profile.resumePersonalization");
  const isTalentNetwork = source === "talent_network";
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [profile, setProfile] = useState(draft.editableProfile);
  const [ownerExternalId, setOwnerExternalId] = useState(draft.ownerExternalId);
  const [externalId, setExternalId] = useState(draft.externalId);
  const [verifiedSession, setVerifiedSession] =
    useState<CandidateProfileVerification | null>(session);
  const [savedProfile, setSavedProfile] = useState(
    session?.authenticated && session.externalId === draft.externalId
      ? session.editableProfile
      : undefined,
  );
  const [saving, setSaving] = useState(false);
  const [editingContact, setEditingContact] =
    useState<CandidateContactField | null>(null);
  const [deliveryState, setDeliveryState] =
    useState<CandidateContactDeliveryState>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const hasChanges =
    candidateProfileFingerprint(profile) !==
    candidateProfileFingerprint(savedProfile);
  const phoneVerified = hasVerifiedCandidatePhone(
    verifiedSession,
    externalId,
    profile,
  );
  const emailVerified = hasVerifiedCandidateEmail(
    verifiedSession,
    externalId,
    profile.email,
  );
  const needsVerification = !phoneVerified || !emailVerified;
  const [openToOpportunities, setOpenToOpportunities] = useState(
    draft.openToOpportunities ?? false,
  );
  const refreshSession = controller.refreshSession;
  const loadVerificationSession = useCallback(
    () =>
      isTalentNetwork
        ? fetchTalentNetworkSession(username)
        : fetchCandidateSession(),
    [isTalentNetwork, username],
  );

  useEffect(() => {
    if (saving || verificationOpen) return;
    let active = true;
    let request = 0;
    const refresh = () => {
      const current = ++request;
      void loadVerificationSession()
        .then((next) => {
          if (active && current === request)
            setVerifiedSession((previous) =>
              !next.authenticated &&
              previous?.authenticated === false &&
              "source" in previous
                ? previous
                : next,
            );
        })
        .catch(() => undefined);
    };
    window.addEventListener("focus", refresh);
    return () => {
      active = false;
      window.removeEventListener("focus", refresh);
    };
  }, [loadVerificationSession, verificationOpen, saving]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("verify") === "phone") {
      if (!phoneVerified) {
        // An email-link redirect requests this popup after session hydration.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setVerificationOpen(true);
      }
      url.searchParams.delete("verify");
      window.history.replaceState(
        window.history.state,
        "",
        url.pathname + url.search,
      );
    }
  }, [phoneVerified]);

  const completeVerification = useCallback(() => {
    setVerificationOpen(false);
    clearCandidateProfileDraft(username, source);
    try {
      if (isTalentNetwork)
        sessionStorage.removeItem(
          TALENT_NETWORK_CONSENT_STORAGE_PREFIX + username,
        );
    } catch {
      /* The saved membership is authoritative. */
    }
    void refreshSession().catch(() => undefined);
    router.replace(
      `/@${encodeURIComponent(username)}${isTalentNetwork ? `?${TALENT_NETWORK_JOINED_QUERY_PARAM}=1` : ""}`,
    );
  }, [isTalentNetwork, refreshSession, router, source, username]);

  const rememberProfile = (next = profile) => {
    saveCandidateProfileDraft(username, source, {
      externalId,
      ownerExternalId,
      editableProfile: next,
      ...(isTalentNetwork && { openToOpportunities }),
    });
  };

  const rememberSavedProfile = (result: CandidateContactStatusDto) => {
    setExternalId(result.externalId);
    setVerifiedSession(result);
    setProfile(result.editableProfile);
    setSavedProfile(result.editableProfile);
    if (
      result.authenticated ??
      (result.emailVerified || result.phoneVerified)
    ) {
      clearCandidateProfileDraft(username, source);
      return;
    }
    saveCandidateProfileDraft(username, source, {
      externalId: result.externalId,
      ownerExternalId,
      editableProfile: result.editableProfile,
      ...(isTalentNetwork && { openToOpportunities }),
    });
  };

  const requestIdentitySignIn = () => {
    setVerificationOpen(false);
    setUploadOpen(false);
    setSaveError(null);
    setSignInOpen(true);
  };

  const completeSignIn = async () => {
    const signedIn = await loadVerificationSession();
    if (!signedIn.authenticated || !signedIn.externalId || !signedIn.editableProfile)
      throw new Error(messages.common.candidateSignInProfileUnavailable);
    setOwnerExternalId(signedIn.externalId);
    if (isTalentNetwork)
      setOpenToOpportunities("openToOpportunities" in signedIn ? signedIn.openToOpportunities ?? false : false);
    setDeliveryState({});
    setSaveError(null);
    rememberSavedProfile({
      authenticated: true,
      externalId: signedIn.externalId,
      source,
      editableProfile: signedIn.editableProfile,
      emailVerified: hasVerifiedCandidateEmail(signedIn, signedIn.externalId, signedIn.editableProfile.email),
      emailLinkSent: false,
      phoneVerified: hasVerifiedCandidatePhone(signedIn, signedIn.externalId, signedIn.editableProfile),
    });
    setSignInOpen(false);
    setUploadOpen(false);
  };

  const saveProfile = async () => {
    rememberProfile();
    setSaveError(null);
    setSaving(true);
    try {
      const result = await startCandidateContactVerification(username, {
        externalId,
        posthogDistinctId: controller.posthogDistinctId || externalId,
        source,
        editableProfile: profile,
        openToOpportunities,
        sendEmailLink: false,
      });
      rememberSavedProfile(result);
      return result;
    } finally {
      setSaving(false);
    }
  };

  const requestVerification = () => {
    rememberProfile();
    if (
      !isValidWhatsAppNumber(
        normalizePhoneInputValue(
          profile.phone,
          normalizeCountryCode(profile.location?.country || ""),
        ),
      )
    ) {
      setSaveError(talentNetworkText("phone_required_before_continuing"));
      setEditingContact("phone");
      return;
    }
    setSaveError(null);
    setVerificationOpen(true);
  };

  const submitProfile = async () => {
    if (needsVerification) {
      requestVerification();
      return;
    }
    if (!hasChanges) {
      if (!isTalentNetwork) completeVerification();
      return;
    }
    try {
      await saveProfile();
      toast.success(talentNetworkText("profile_saved"));
      void refreshSession().catch(() => undefined);
    } catch (failure) {
      if (isCandidateIdentityConflict(failure)) {
        requestIdentitySignIn();
        return;
      }
      setSaveError(
        failure instanceof Error
          ? failure.message
          : talentNetworkText("unable_to_save_profile"),
      );
    }
  };
  return {
    router,
    t,
    isTalentNetwork,
    verificationOpen,
    setVerificationOpen,
    uploadOpen,
    setUploadOpen,
    signInOpen,
    setSignInOpen,
    requestIdentitySignIn,
    completeSignIn,
    profile,
    setProfile,
    ownerExternalId,
    setOwnerExternalId,
    externalId,
    setExternalId,
    setSavedProfile,
    saving,
    editingContact,
    setEditingContact,
    deliveryState,
    setDeliveryState,
    saveError,
    setSaveError,
    hasChanges,
    phoneVerified,
    emailVerified,
    needsVerification,
    openToOpportunities,
    setOpenToOpportunities,
    completeVerification,
    rememberProfile,
    rememberSavedProfile,
    submitProfile,
  };
}
